/**
 * 仕様グラフの本文をどの言語で揃えるかの方針。
 *
 * 本文（表示名・説明・業務ロジック・受け入れ条件など、人が読む文字列）は 1 言語では足りない。
 * 仕様を読む側に日本語話者とベトナム語話者の両方がいるため、**同じ事実が全言語で揃っている**
 * ことを構造で保証する。揃っていない状態を表現できてしまうと、読む言語によって仕様の内容が
 * 違うという最悪の形の乖離が起きる。
 *
 * 「どの言語が揃っていれば完成か」の定義をこのファイル 1 箇所に置くのは、書込経路が複数ある
 * ためである。MCP の書込ツール・入力スキーマ・読取契約・画面の言語切替が別々に言語一覧を
 * 持つと、経路ごとに揃う言語が変わり、片方だけ増えた言語が黙って抜け落ちる（制約 11 —
 * 同じ事実の 2 表現）。
 *
 * knowledge-wiki も同じ構造の定数（`SYNCED_LOCALES`）を持つが、**共有しない**。あちらの
 * 宣言は「読み手がいる言語だけを置く」というドメインごとの判断であり、記事の読者層と仕様の
 * 読者層が将来別々に動きうる。定数を共有すると、片方のドメインの都合で入れた言語が
 * もう片方に強制的に波及する。構造を真似ることと、値を共有することは別である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 仕様グラフの本文を揃える対象 locale の定義と、不足 locale の検出
 * @graph-connects none
 */
/**
 * 本文を常に揃える locale。
 *
 * ベトナム語話者のエンジニアが仕様の読み手にいるため `vi` を含める。ここに locale を足すと、
 * 入力スキーマが要求する言語（`localizedText()`）と、MCP 書込が呼び出し側に要求する言語の
 * **両方**が同時に増える。読み手のいない言語を足すと、誰も読まない訳文がノード数ぶん増えて
 * 保守対象だけが膨らむので、実際に読む人がいる言語だけを置く。
 *
 * 並びは fallback の優先順ではなく、対等な集合である。読取側で未翻訳を埋める規則を持つのは
 * 表示層の関心であり、契約側は「全部揃っている」しか表現しない。
 *
 * @graph-connects none
 */
export const SPEC_LOCALES = ['ja', 'en', 'vi'];
/**
 * 本文の原文を書く locale。
 *
 * **仕様を書くのは日本側の組織であり、これは変わらない**という前提を型に焼いたものである。
 * `en` / `ja` のどちらが原文かを実行時に持ち回る設計（knowledge-wiki は編集中のタブを原文に
 * する）と違い、原文が 1 つに決まっていれば「どの版から訳したか」をレコード側に記録する必要が
 * 無い。派生の向きが常に `ja → en` / `ja → vi` の 1 方向だからである。
 *
 * この定数は単なる既定値ではなく**制約**である。人が使う書込経路（API）は原文の locale しか
 * 受け付けず、他の locale は原文からの派生としてのみ書かれる。前提が崩れる日（日本側以外が
 * 仕様を起こす日）が来たら、ここを変えるだけでは済まず、原文 locale をレコードごとに持つ
 * 設計へ作り直すことになる。その分岐点をこの 1 行に集約してある。
 *
 * @graph-connects none
 */
export const SOURCE_LOCALE = 'ja';
/**
 * 渡された locale 集合に足りないものを返す。
 *
 * MCP の書込経路は自動翻訳せず、呼び出し側に全 locale を要求する。その判定に使う
 * （MCP の `missingLocalesResult` が返す拒否レスポンスの材料）。翻訳基盤へ丸投げしないのは、機械訳が
 * 業務用語と固有名詞を崩すうえ、誰も検収していない訳文が仕様として残るためである。本文を
 * 書いたのは呼び出し側（エージェント）自身なので、各言語も自分で書ける。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 書込時に不足している locale を検出する
 * @graph-connects none
 */
export function findMissingLocales(providedLocales) {
    const provided = new Set(providedLocales);
    return SPEC_LOCALES.filter((locale) => !provided.has(locale));
}
/**
 * 本文 1 つが「中身が無い」かを判定する。**空判定の唯一の所有者**である。
 *
 * 空白だけの文字列を空として扱う。書込側の `nonEmptyString()` は長さしか見ないため、
 * `" "` のような値は全 locale 非空の要件を通り抜けて書き込める。読取側が `=== ''` で
 * 判定すると、その値は「訳がある」と見なされ、未翻訳の印が出ないまま空白が表示される。
 *
 * 経路ごとに判定を書くと必ずこの種のズレが生まれる（実際、MCP は `.trim()` 込み、
 * 読取 API は `=== ''` と割れていた）。判定を 1 箇所へ寄せ、全経路がここを通す。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 本文 1 つが空（空白のみを含む）かを判定する単一の所有者
 * @graph-connects none
 */
export function isBlankText(value) {
    return (value ?? '').trim() === '';
}
/**
 * properties 等の未検証の値から本文 1 組を読む。
 *
 * BQ の `properties` は JSON 文字列を復元しただけの `unknown` であり、そこに全 locale が
 * 揃っているとは限らない（この設計より前に書かれたノードには `vi` が無い）。**1 つでも
 * 欠けていれば `null` を返す** — 部分的に埋まった組をそのまま通すと、読取側が locale ごとに
 * 「有るか無いか」を確かめて回ることになり、判定が呼び出し箇所の数だけ増える。
 *
 * 欠けを「未翻訳」として扱うのは表示層の関心であり、ここは「組として成立しているか」だけを
 * 答える。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 未検証の値から全 locale 揃った本文 1 組を読む（欠けていれば null）
 * @graph-connects none
 */
export function readLocalizedText(source) {
    if (typeof source !== 'object' || source === null)
        return null;
    const record = source;
    const result = {};
    for (const locale of SPEC_LOCALES) {
        const value = record[locale];
        if (typeof value !== 'string')
            return null;
        result[locale] = value;
    }
    return result;
}
/**
 * 組み立てたラベルのうち、**空白しか残らなかった言語を空文字へ畳む。**
 *
 * ラベルは語をつないで作るので（`labels.ts`）、材料の語がその言語で 1 つも訳されていないと
 * 区切りの空白だけが残る（英語・ベトナム語の `'pick '` → `' '`）。空白だけの表示名は画面で
 * 「名前はあるが読めない」として出てしまい、未記入への差し替え（`displayLabel` は `=== ''`
 * で判定する）を素通りする。
 *
 * **畳むのは空白だけになった言語に限る。** 語が 1 つでも残っていれば手を触れない — 日本語の
 * `'選定対象を'`（動詞だけ未訳）は読めば欠けが分かるが、そこまで空へ倒すと「何も書かれて
 * いない」と「一部が未訳」が画面で同じ見た目になる。
 *
 * **ラベルを組み立てる読取はすべてここを通す。** 読取ごとに畳み方を書くと、画面によって
 * 同じノードが訳済みに見えたり未訳に見えたりする。
 *
 * @param text 組み立てたラベル
 * @returns 空白しか無い言語を空文字にしたラベル
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 組み立てたラベルのうち空白しか残らなかった言語を空文字へ畳む
 * @graph-connects isBlankText [calls] 本文 1 つが空かの判定
 */
export function compactLocalizedText(text) {
    const compacted = { ...text };
    for (const locale of SPEC_LOCALES) {
        if (isBlankText(compacted[locale]))
            compacted[locale] = '';
    }
    return compacted;
}
//# sourceMappingURL=locales.js.map